/** Platform admin: organisations, licences, branding, restoring deleted projects, audit log. */
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { keys, useAudit, useDeletedProjects, useMe, useOrgs } from "../api/hooks";
import type { Org } from "../api/types";
import { StageIntro } from "../components/sat";
import { ConfirmDialog, ErrorNotice, Field, FileDrop, Notice, Spinner } from "../components/ui";
import { uploadFile } from "../lib/files";
import { dateTime, day } from "../lib/format";

interface OrgForm {
  name: string;
  type: "seat" | "unlimited";
  seats: string;
  valid_until: string;
  users_can_manage_users: boolean;
  issuer_name: string;
  timezone: string;
}

const EMPTY: OrgForm = {
  name: "",
  type: "seat",
  seats: "5",
  valid_until: "",
  users_can_manage_users: false,
  issuer_name: "Nordic Smart Grid Solutions",
  timezone: "Europe/Copenhagen",
};

function toForm(org: Org): OrgForm {
  return {
    name: org.name,
    type: org.licence.type,
    seats: org.licence.seats?.toString() ?? "5",
    valid_until: org.licence.valid_until ?? "",
    users_can_manage_users: org.licence.users_can_manage_users,
    issuer_name: org.branding.issuer_name,
    timezone: String((org.defaults as { timezone?: string }).timezone ?? "Europe/Copenhagen"),
  };
}

function payload(form: OrgForm) {
  return {
    name: form.name.trim(),
    licence: {
      type: form.type,
      seats: form.type === "seat" ? Number(form.seats) : null,
      valid_until: form.valid_until || null,
      users_can_manage_users: form.type === "unlimited" && form.users_can_manage_users,
    },
    branding: { issuer_name: form.issuer_name.trim() },
    defaults: { timezone: form.timezone.trim() },
  };
}

function OrgFields({ form, onChange }: { form: OrgForm; onChange: (next: OrgForm) => void }) {
  const set = <K extends keyof OrgForm>(field: K, value: OrgForm[K]) => onChange({ ...form, [field]: value });
  return (
    <>
      <div className="grid-3">
        <Field label="Organisation name">
          <input className="input" required maxLength={120} value={form.name} onChange={(event) => set("name", event.target.value)} />
        </Field>
        <Field label="Licence">
          <select className="input" value={form.type} onChange={(event) => set("type", event.target.value as OrgForm["type"])}>
            <option value="seat">Seats (fixed number of users)</option>
            <option value="unlimited">Unlimited users</option>
          </select>
        </Field>
        {form.type === "seat" ? (
          <Field label="User seats" hint="Clients do not use a seat.">
            <input className="input" type="number" min={1} step={1} required value={form.seats} onChange={(event) => set("seats", event.target.value)} />
          </Field>
        ) : (
          <label className="checkbox" style={{ alignSelf: "end" }}>
            <input type="checkbox" checked={form.users_can_manage_users} onChange={(event) => set("users_can_manage_users", event.target.checked)} />
            <span>Users may invite and deactivate users</span>
          </label>
        )}
        <Field label="Licence valid until" hint="Empty: no end date.">
          <input className="input" type="date" value={form.valid_until} onChange={(event) => set("valid_until", event.target.value)} />
        </Field>
        <Field label="Report issuer" hint="Printed on the reports and workbooks.">
          <input className="input" required maxLength={120} value={form.issuer_name} onChange={(event) => set("issuer_name", event.target.value)} />
        </Field>
        <Field label="Default time zone" hint="IANA name; new projects start with it.">
          <input className="input" required value={form.timezone} onChange={(event) => set("timezone", event.target.value)} />
        </Field>
      </div>
    </>
  );
}

function OrgCard({ org }: { org: Org }) {
  const client = useQueryClient();
  const [form, setForm] = useState<OrgForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => client.invalidateQueries({ queryKey: keys.orgs });

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/orgs/${org.id}`, { version: org.version, ...body });
      setForm(null);
      await refresh();
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  async function logo(file: File) {
    setError(null);
    try {
      const uploaded = await uploadFile(`/orgs/${org.id}/uploads`, file, "branding", {}, setProgress);
      await patch({ branding: { issuer_logo: uploaded.key } });
    } catch (reason) {
      setError(reason);
    } finally {
      setProgress(null);
    }
  }

  const conflict = error instanceof ApiError && error.code === "version_conflict";
  return (
    <div className="card stack">
      <div className="card-head">
        <div>
          <h3>{org.name}</h3>
          <p>
            {org.licence.type === "seat" ? `${org.seats_used} of ${org.licence.seats} user seats` : "Unlimited users"}
            {org.licence.valid_until ? ` · valid until ${day(org.licence.valid_until)}` : ""} · issuer {org.branding.issuer_name}
            {org.branding.issuer_logo ? " (own logo)" : " (NSGS logo)"}
          </p>
        </div>
        <div className="row">
          <Link className="btn small" to={`/org/members?org=${org.id}`}>
            Members
          </Link>
          {!form && (
            <button className="btn small" onClick={() => setForm(toForm(org))}>
              Edit
            </button>
          )}
        </div>
      </div>
      {form && (
        <form
          className="stack"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void patch(payload(form));
          }}
        >
          <OrgFields form={form} onChange={setForm} />
          <FileDrop
            accept="image/png,image/jpeg,image/webp"
            disabled={!!progress || busy}
            onFiles={(files) => files[0] && logo(files[0])}
            label={progress ? <Spinner label={progress.replace(/…$/, "")} /> : <span className="small">Drop an issuer logo (PNG, JPG or WEBP) to replace the NSGS logo on this organisation's reports.</span>}
          />
          <div className="row end">
            {org.branding.issuer_logo && (
              <button type="button" className="btn small ghost" disabled={busy} onClick={() => patch({ branding: { issuer_logo: null } })}>
                Use the NSGS logo
              </button>
            )}
            <button type="button" className="btn" onClick={() => { setForm(null); setError(null); }}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}
      <ErrorNotice error={error} title={conflict ? "This organisation changed in the meantime" : undefined} />
      {conflict && (
        <div>
          <button className="btn small" onClick={() => { setError(null); setForm(null); void refresh(); }}>
            Reload
          </button>
        </div>
      )}
    </div>
  );
}

function CreateOrg() {
  const client = useQueryClient();
  const [form, setForm] = useState<OrgForm>(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!open) {
    return (
      <div>
        <button className="btn primary" onClick={() => setOpen(true)}>
          + New organisation
        </button>
      </div>
    );
  }
  return (
    <form
      className="card stack"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.post("/admin/orgs", payload(form));
          await client.invalidateQueries({ queryKey: keys.orgs });
          setForm(EMPTY);
          setOpen(false);
        } catch (reason) {
          setError(reason);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h3>New organisation</h3>
      <OrgFields form={form} onChange={setForm} />
      <ErrorNotice error={error} />
      <div className="row end">
        <button type="button" className="btn" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy}>
          {busy ? "Creating…" : "Create organisation"}
        </button>
      </div>
    </form>
  );
}

function DeletedProjects({ orgs }: { orgs: Org[] }) {
  const client = useQueryClient();
  const deleted = useDeletedProjects();
  if (deleted.isLoading) return <Spinner label="Loading deleted projects" />;
  if (deleted.error) return <ErrorNotice error={deleted.error} />;
  if (!deleted.data!.length) return <p className="muted small">No deleted projects.</p>;
  return (
    <ul className="file-list">
      {deleted.data!.map((item) => (
        <li key={item.project_id}>
          <span>
            <strong>{item.label}</strong>
            <span className="muted small">
              {" "}
              · {orgs.find((org) => org.id === item.org_id)?.name ?? item.org_id} · deleted {dateTime(item.deleted_at, "Europe/Copenhagen")}
            </span>
          </span>
          <ConfirmDialog
            trigger={<button className="btn small">Restore</button>}
            title={`Restore ${item.label}?`}
            description="The project becomes visible again with all its evidence, results and reports."
            confirmLabel="Restore project"
            onConfirm={async () => {
              await api.post(`/projects/${item.project_id}/restore`);
              await Promise.all([client.invalidateQueries({ queryKey: keys.deleted }), client.invalidateQueries({ queryKey: keys.projects })]);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

function AuditLog({ orgs }: { orgs: Org[] }) {
  const [scope, setScope] = useState<string>("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const audit = useAudit(scope || null, date, open);
  return (
    <details className="disclosure" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Audit log</summary>
      <div className="stack">
        <div className="row">
          <Field label="Scope">
            <select className="input" value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="">Platform</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Day (UTC)">
            <input className="input" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
        </div>
        {audit.isLoading && <Spinner label="Loading the audit log" />}
        <ErrorNotice error={audit.error} />
        {audit.data && audit.data.length === 0 && <p className="muted small">No entries on this day.</p>}
        {audit.data && audit.data.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>By</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.map((entry, index) => (
                  <tr key={`${entry.at}-${index}`}>
                    <td>{dateTime(entry.at, "UTC")}</td>
                    <td>{entry.action}</td>
                    <td className="mono">{entry.email ?? entry.uid ?? "system"}</td>
                    <td className="mono small">{JSON.stringify(entry.target ?? {})}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  );
}

export function AdminOrgsPage() {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const orgs = useOrgs(isAdmin);
  if (me.data && !isAdmin) return <Notice kind="info">This page is for platform administrators.</Notice>;
  if (orgs.isLoading) return <Spinner label="Loading organisations" />;
  if (orgs.error) return <ErrorNotice error={orgs.error} />;
  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <StageIntro title="Organisations" description="Licensees of SAT Analyzer: licence, seats and report branding." status={`${orgs.data!.length} organisation(s)`} />
      <CreateOrg />
      {orgs.data!.map((org) => (
        <OrgCard key={`${org.id}:${org.version}`} org={org} />
      ))}
      <section className="card stack">
        <h3>Deleted projects</h3>
        <DeletedProjects orgs={orgs.data!} />
      </section>
      <AuditLog orgs={orgs.data!} />
    </div>
  );
}
